/**
 * PatientBulkUploadDialog — CSV / native-PDF import with preview → tick → commit flow.
 *
 * Uses the /api/patients/bulk-upload/preview + /commit endpoints
 * shipped in patient_bulk.py. Legacy async /api/import/* modal
 * still exists for employees.
 *
 * Three steps:
 *   1. Drop file (CSV or native-text PDF).
 *   2. Review preview table — tick which rows to import, filter by
 *      importable / warnings / errors.
 *   3. Success screen with per-row outcomes + "back to patients"
 *      or "check eligibility on all" quick-actions.
 */
import React, { useMemo, useState } from 'react';
import {
  CheckCircle,
  CircleNotch,
  FileArrowUp,
  Warning,
  WarningOctagon,
  XCircle,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Checkbox } from '../ui/checkbox';
import api from '../../services/api';

const MAX_MB = 15;
const ACCEPTED = '.csv,.xlsx,.xls,.pdf,.docx,.doc,text/csv,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword';

const STEPS = ['Upload', 'Review & pick', 'Complete'];

const StepBar = ({ step }) => (
  <div className="flex items-center justify-center gap-2 pb-4" data-testid="bulk-upload-step-bar">
    {STEPS.map((label, i) => (
      <React.Fragment key={label}>
        <div
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
            i + 1 === step
              ? 'bg-indigo-100 text-indigo-800 font-semibold'
              : i + 1 < step
                ? 'bg-emerald-100 text-emerald-800'
                : 'bg-slate-100 text-slate-500'
          }`}
        >
          <span>{i + 1}. {label}</span>
        </div>
        {i < STEPS.length - 1 && (
          <div className={`w-6 h-px ${i + 1 < step ? 'bg-emerald-300' : 'bg-slate-200'}`} />
        )}
      </React.Fragment>
    ))}
  </div>
);

const SeverityIcon = ({ severity, size = 14 }) => {
  if (severity === 'critical' || severity === 'error') {
    return <XCircle size={size} className="text-rose-600 flex-shrink-0" />;
  }
  if (severity === 'warning') {
    return <Warning size={size} className="text-amber-600 flex-shrink-0" />;
  }
  return <CheckCircle size={size} className="text-emerald-600 flex-shrink-0" />;
};

// -------------------------- step 1 -------------------------- //
const UploadStep = ({ file, setFile, onParse, parsing }) => {
  const [dragging, setDragging] = useState(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) setFile(dropped);
  };
  return (
    <div className="space-y-4">
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
          dragging ? 'border-indigo-500 bg-indigo-50' : 'border-slate-300'
        }`}
        data-testid="bulk-upload-drop-zone"
      >
        <FileArrowUp size={40} className="mx-auto mb-3 text-slate-400" />
        <p className="text-sm text-slate-700 font-medium">
          {file ? file.name : 'Drop a CSV, Excel, PDF, or Word file here, or click to browse'}
        </p>
        <p className="text-xs text-slate-500 mt-1">
          Max {MAX_MB} MB · CSV, Excel, PDF, or Word — scanned documents are read with OCR
        </p>
        <label className="inline-block mt-4">
          <input
            type="file"
            accept={ACCEPTED}
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="bulk-upload-file-input"
          />
          <Button
            variant="outline"
            size="sm"
            asChild
          >
            <span>Choose file</span>
          </Button>
        </label>
      </div>
      <div className="text-xs text-slate-500 space-y-1">
        <p><strong>Column names we'll recognize automatically</strong> — pick any of these variants:</p>
        <p>
          <em>Name</em>: first_name / firstname / given_name · last_name / surname / family_name · full_name / patient_name / client_name
          <br />
          <em>Medicaid ID</em>: medicaid_id / mcd_id / recipient_id / recipient / member_id / case_number
          <br />
          <em>DOB</em>: dob / date_of_birth / birthdate · <em>Address</em>: address / street / street_address · <em>ZIP</em>: zip / postal_code
        </p>
        <p>Unrecognized columns are ignored — nothing is silently written to the patient record.</p>
      </div>
      <div className="flex justify-end">
        <Button
          onClick={onParse}
          disabled={!file || parsing}
          data-testid="bulk-upload-parse-btn"
        >
          {parsing
            ? <><CircleNotch size={16} className="mr-1.5 animate-spin" />Analyzing…</>
            : 'Preview'}
        </Button>
      </div>
    </div>
  );
};

// -------------------------- step 2 -------------------------- //
const ReviewStep = ({ preview, selected, setSelected, filterMode, setFilterMode, onCommit, committing }) => {
  const rows = preview.rows;

  const filtered = useMemo(() => {
    if (filterMode === 'importable') return rows.filter((r) => r.is_importable);
    if (filterMode === 'warned') return rows.filter((r) => r.is_importable && r.warnings.length > 0);
    if (filterMode === 'errors') return rows.filter((r) => !r.is_importable);
    return rows;
  }, [rows, filterMode]);

  const toggleAllVisible = () => {
    const visibleImportable = filtered.filter((r) => r.is_importable).map((r) => r.source_index);
    const allSelected = visibleImportable.every((i) => selected.has(i));
    const next = new Set(selected);
    if (allSelected) {
      visibleImportable.forEach((i) => next.delete(i));
    } else {
      visibleImportable.forEach((i) => next.add(i));
    }
    setSelected(next);
  };

  const toggleRow = (source_index) => {
    const next = new Set(selected);
    if (next.has(source_index)) next.delete(source_index);
    else next.add(source_index);
    setSelected(next);
  };

  return (
    <div className="space-y-3">
      {/* stats + filter chips */}
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <div className="flex gap-2 flex-wrap" data-testid="bulk-upload-summary">
          <Badge variant="secondary">
            {preview.summary.total} rows · {preview.summary.importable} importable
          </Badge>
          {preview.summary.with_errors > 0 && (
            <Badge className="bg-rose-100 text-rose-800">
              {preview.summary.with_errors} error{preview.summary.with_errors > 1 ? 's' : ''}
            </Badge>
          )}
          {preview.summary.with_warnings > 0 && (
            <Badge className="bg-amber-100 text-amber-800">
              {preview.summary.with_warnings} warning{preview.summary.with_warnings > 1 ? 's' : ''}
            </Badge>
          )}
          {preview.summary.duplicates_against_existing > 0 && (
            <Badge className="bg-indigo-100 text-indigo-800">
              {preview.summary.duplicates_against_existing} duplicate{preview.summary.duplicates_against_existing > 1 ? 's' : ''} of existing
            </Badge>
          )}
        </div>
        <div className="flex gap-1" data-testid="bulk-upload-filter-chips">
          {[
            ['all', `All (${rows.length})`],
            ['importable', `Importable (${preview.summary.importable})`],
            ['warned', `Warnings (${preview.summary.with_warnings})`],
            ['errors', `Errors (${preview.summary.with_errors})`],
          ].map(([mode, label]) => (
            <Button
              key={mode}
              size="sm"
              variant={filterMode === mode ? 'default' : 'outline'}
              onClick={() => setFilterMode(mode)}
              data-testid={`bulk-upload-filter-${mode}`}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>

      <div className="border rounded-lg overflow-x-auto max-h-[400px] overflow-y-auto">
        <Table>
          <TableHeader className="sticky top-0 bg-white shadow-sm">
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  onCheckedChange={toggleAllVisible}
                  aria-label="Toggle all visible importable rows"
                  data-testid="bulk-upload-toggle-all"
                />
              </TableHead>
              <TableHead className="w-12">Row</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>DOB</TableHead>
              <TableHead>Medicaid ID</TableHead>
              <TableHead>Address</TableHead>
              <TableHead>Flags</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((r) => {
              const isSelected = selected.has(r.source_index);
              const rowStyle = !r.is_importable
                ? 'bg-rose-50/40'
                : r.warnings.length > 0
                  ? 'bg-amber-50/30'
                  : '';
              return (
                <TableRow
                  key={r.source_index}
                  className={rowStyle}
                  data-testid={`bulk-upload-row-${r.source_index}`}
                >
                  <TableCell>
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleRow(r.source_index)}
                      disabled={!r.is_importable}
                      aria-label={`Include row ${r.source_index}`}
                      data-testid={`bulk-upload-row-checkbox-${r.source_index}`}
                    />
                  </TableCell>
                  <TableCell className="text-xs font-mono">{r.source_index}</TableCell>
                  <TableCell className="text-sm">
                    {r.normalized.first_name || <em className="text-slate-400">?</em>}{' '}
                    {r.normalized.last_name || <em className="text-slate-400">?</em>}
                  </TableCell>
                  <TableCell className="text-sm font-mono">
                    {r.normalized.date_of_birth || <em className="text-slate-400">?</em>}
                  </TableCell>
                  <TableCell className="text-sm font-mono">
                    {r.normalized.medicaid_id || <em className="text-slate-400">?</em>}
                  </TableCell>
                  <TableCell className="text-xs">
                    {[r.normalized.city, r.normalized.state, r.normalized.zip_code]
                      .filter(Boolean).join(', ') || <em className="text-slate-400">missing</em>}
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      {r.errors.map((f) => (
                        <div
                          key={f.code}
                          className="flex items-start gap-1.5 text-xs text-rose-700"
                          title={f.message}
                        >
                          <SeverityIcon severity="error" />
                          <span>{f.message}</span>
                        </div>
                      ))}
                      {r.warnings.map((f) => (
                        <div
                          key={f.code}
                          className="flex items-start gap-1.5 text-xs text-amber-700"
                          title={f.message}
                        >
                          <SeverityIcon severity="warning" />
                          <span>{f.message}</span>
                        </div>
                      ))}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-sm text-slate-500 py-6">
                  No rows match the current filter.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between pt-2">
        <p className="text-sm text-slate-600" data-testid="bulk-upload-selection-count">
          {selected.size} row{selected.size === 1 ? '' : 's'} selected for import
        </p>
        <Button
          onClick={onCommit}
          disabled={selected.size === 0 || committing}
          data-testid="bulk-upload-commit-btn"
        >
          {committing
            ? <><CircleNotch size={16} className="mr-1.5 animate-spin" />Importing…</>
            : `Import ${selected.size} patient${selected.size === 1 ? '' : 's'}`}
        </Button>
      </div>
    </div>
  );
};

// -------------------------- step 3 -------------------------- //
const CompleteStep = ({ result, onClose }) => {
  const createdCount = result.created;
  const errorCount = result.results?.filter((r) => r.status === 'error').length || 0;
  return (
    <div className="text-center py-6 space-y-3" data-testid="bulk-upload-complete-step">
      {createdCount > 0 ? (
        <CheckCircle size={48} weight="fill" className="mx-auto text-emerald-500" />
      ) : (
        <WarningOctagon size={48} weight="fill" className="mx-auto text-rose-500" />
      )}
      <h3 className="text-lg font-semibold">
        {createdCount} patient{createdCount === 1 ? '' : 's'} imported
      </h3>
      {errorCount > 0 && (
        <p className="text-sm text-rose-700">
          {errorCount} row{errorCount === 1 ? '' : 's'} failed — check server logs.
        </p>
      )}
      {result.skipped_not_importable > 0 && (
        <p className="text-sm text-slate-600">
          {result.skipped_not_importable} row{result.skipped_not_importable === 1 ? '' : 's'} skipped (errors unresolved).
        </p>
      )}
      <div className="flex justify-center gap-2 pt-3">
        <Button onClick={onClose} data-testid="bulk-upload-done-btn">Done</Button>
      </div>
    </div>
  );
};

// -------------------------- root --------------------------- //
export const PatientBulkUploadDialog = ({ isOpen, onClose, onImportComplete }) => {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [filterMode, setFilterMode] = useState('all');
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);

  const handleClose = () => {
    // Trigger a second refetch on close in case the initial post-commit
    // fetch raced with another in-flight list-fetch and got clobbered.
    // Cheap and belt-and-suspenders vs. a stale patient list.
    if (result && result.created > 0) {
      onImportComplete?.();
    }
    setStep(1);
    setFile(null);
    setParsing(false);
    setPreview(null);
    setSelected(new Set());
    setFilterMode('all');
    setCommitting(false);
    setResult(null);
    onClose?.();
  };

  const handleParse = async () => {
    if (!file) return;
    setParsing(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post('/patients/bulk-upload/preview', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 240000,
      });
      setPreview(res.data);
      // Pre-select every importable + non-duplicate row so the
      // supervisor only has to tick DOWN, not tick UP.
      const auto = new Set(
        res.data.rows
          .filter((r) => r.is_importable && !r.duplicate_of)
          .map((r) => r.source_index),
      );
      setSelected(auto);
      setStep(2);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(`Preview failed: ${detail}`);
    } finally {
      setParsing(false);
    }
  };

  const handleCommit = async () => {
    if (!preview || selected.size === 0) return;
    setCommitting(true);
    try {
      const res = await api.post('/patients/bulk-upload/commit', {
        session_id: preview.session_id,
        selected_source_indices: Array.from(selected),
      });
      setResult(res.data);
      setStep(3);
      toast.success(
        `Imported ${res.data.created} patient${res.data.created === 1 ? '' : 's'}.`,
      );
      onImportComplete?.();
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(`Import failed: ${detail}`);
    } finally {
      setCommitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => (!o ? handleClose() : null)}>
      <DialogContent
        className="max-w-5xl max-h-[85vh] overflow-y-auto"
        data-testid="patient-bulk-upload-dialog"
      >
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Manrope' }}>Bulk Import Patients</DialogTitle>
          <DialogDescription>
            Upload a CSV, Excel, PDF (scanned is fine), or Word file from your prior EHR — we'll parse it,
            flag issues, and let you review each row before committing.
          </DialogDescription>
        </DialogHeader>

        <StepBar step={step} />

        {step === 1 && (
          <UploadStep
            file={file}
            setFile={setFile}
            onParse={handleParse}
            parsing={parsing}
          />
        )}
        {step === 2 && preview && (
          <ReviewStep
            preview={preview}
            selected={selected}
            setSelected={setSelected}
            filterMode={filterMode}
            setFilterMode={setFilterMode}
            onCommit={handleCommit}
            committing={committing}
          />
        )}
        {step === 3 && result && (
          <CompleteStep result={result} onClose={handleClose} />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PatientBulkUploadDialog;
